import type { Sheet2JSONOpts } from 'xlsx';

type XlsxRowValue = string | number | boolean | Date | null;
export type XlsxRow = Record<string, XlsxRowValue>;
type GetFromXlsxCallback<Result> = (error: Error | null, rows: XlsxRow[]) => Result;
type SheetToJsonOptions = Sheet2JSONOpts & {
  skipHeader: boolean;
  defval: string;
};

interface GetFromXlsxOptionsBase {
  file: Blob | null;
  worksheet?: number;
  sheetIndex?: number;
  rowStart?: number;
  columnStart?: number;
  columnEnd?: number | null;
  maxRows?: number;
}

type GetFromXlsxOptionsWithoutCallback = GetFromXlsxOptionsBase & {
  componentCallback?: null;
};

type GetFromXlsxOptionsWithCallback<Result> = GetFromXlsxOptionsBase & {
  componentCallback: GetFromXlsxCallback<Result>;
};

export default function getFromXlsx(options: GetFromXlsxOptionsWithoutCallback): Promise<XlsxRow[] | null>;
export default function getFromXlsx<Result>(options: GetFromXlsxOptionsWithCallback<Result>): Promise<Result | null>;
export default async function getFromXlsx<Result = XlsxRow[]>({
  file,

  componentCallback = null,
  worksheet: legacyWorksheet,
  sheetIndex = 1, // 1-indexed
  rowStart = 1, // 1-indexed
  columnStart = 1, // 1-indexed
  columnEnd = null, // 1-indexed
  maxRows = 1000, // Data-rows only
}: GetFromXlsxOptionsWithoutCallback | GetFromXlsxOptionsWithCallback<Result>): Promise<Result | XlsxRow[] | null> {
  const error = null;

  // Handle if the form was submitted with no file - dont do anything
  if (file === null) {
    return null;
  }

  // Use fileReader to access the file data structure
  const fileToData = async (fileToLoad: Blob): Promise<Uint8Array> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = (fileResult) => {
        if (!(fileResult.target?.result instanceof ArrayBuffer)) {
          reject(new Error('Unable to read xlsx file'));
          return;
        }
        const fileDataString = new Uint8Array(fileResult.target.result);
        resolve(fileDataString);
      };
      reader.onerror = () => reject(new Error('Unable to read xlsx file'));
      reader.readAsArrayBuffer(fileToLoad);
    });
  };

  // Now load in the data, and lazyLoad xlsx library
  const fileDataString = await fileToData(file);
  const xlsxReader = await import('xlsx');
  const workbook = xlsxReader.read(fileDataString, { type: 'array' });

  // Reduce 1 from sheetIndex, this was done so all ints were consistently 1-indexed like excel
  const worksheetIndex = legacyWorksheet === undefined ? sheetIndex : legacyWorksheet + 1;
  const worksheet = workbook.Sheets[workbook.SheetNames[worksheetIndex - 1]];

  // Access the workbook using the dataset
  const range = xlsxReader.utils.decode_range(worksheet['!ref'] ?? '');

  // The offset of 2 takes into account the 0-indexing
  // And the fact its a skip rows under X, rather than start at Y
  range.s.r = rowStart - 2;
  range.s.c = columnStart - 1;
  if (columnEnd !== null) {
    range.e.c = columnEnd;
  }
  worksheet['!ref'] = xlsxReader.utils.encode_range(range);

  // Load the relevant sheet & convert to json
  const sheetOptions: SheetToJsonOptions = { skipHeader: true, defval: '' };
  const rows = xlsxReader.utils.sheet_to_json<XlsxRow>(worksheet, sheetOptions);

  // If the callbacks are being used, use themo otherwise just return out
  if (!componentCallback) {
    return rows.slice(0, maxRows);
  }
  return componentCallback(error, rows.slice(0, maxRows));
}
